import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { ActivatedRoute, Router } from "@angular/router";
import {
  APIErrorModel,
  BaseResponse,
  ParametersOfPagination,
} from "../../../shared/models/base-response.model";
import { UserEventStatusRespModel } from "../../profile/profile.model";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { UserService } from "../../../core/services/user.service";
import { DigitalCatalogueBllService } from "../digital-catalogue-bll.service";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import {
  CatalogueAdsResponseModel,
  CompanyListModel,
  ParamsToGetAdsList,
  ParamsToGetCompanyList,
  listCompanyModel,
} from "../digital-catalogue.model";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { AuthBllService } from "../../auth/auth-bll.service";
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { CatalogueAdsComponent } from "../catalogue-ads/catalogue-ads.component";
import { HomeBllService } from "../../home/home-bll.service";
import { EventDetailsResponseModel } from "../../home/home.model";

@Component({
  selector: "app-catalogue-companies-list",
  imports: [
    HeaderComponent,
    LeftSideNavComponent,
    CommonModule,
    FormsModule,
    InfiniteScrollDirective,
    CatalogueAdsComponent
  ],
  templateUrl: "./catalogue-companies-list.component.html",
  styleUrls: ["./catalogue-companies-list.component.scss"],
})
export class CatalogueCompaniesListComponent implements OnInit, OnDestroy {
  isMobileDevice: boolean = false;
  companiesList: CompanyListModel = {
    currentpage: 1,
    enditem: 0,
    max_id: 0,
    pagescount: 1,
    shuffled_index1: 0,
    shuffled_index2: 0,
    startitem: 0,
    totalcount: 0,
    companies_count: 0,
    products_count: 0,
    list: [], // Array of company models
    total_count: 0,
  };

  paramsToFetchData: ParamsToGetCompanyList = {
    pavilion_code: "",
    shuffled_index1: 0,
    shuffled_index2: 0,
    max_id: 0,
    currentpage: 1,
    search: "",
    category: "",
  };
  paginationParams: ParametersOfPagination = {
    totalCount: 0,
    pagesCount: 1,
    pageSize: 10,
    startItem: 0,
    endItem: 0,
  };
  pavilionImgUrl: string = "";
  pavilionName: string = "";
  searchDebounce: ReturnType<typeof setTimeout> | null = null;

  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;
  isUserStatusLoading = false;

  searchText: string = "";
  isSearchOpen = false;
  isDataLoaded: boolean = false;
  categoryName: string = "";
  categoryImgUrl: string = "";

  @ViewChild("searchInput") searchInput!: ElementRef;

  // User authentication status
  isAuthenticated: boolean = false;
  isLoadingMore: boolean = false;
  isLoaded: boolean = false;

  eventDetails: EventDetailsResponseModel | null = null;
  
  constructor(
    private profileBll: ProfileBllService,
    private userService: UserService,
    private catalogueBllService: DigitalCatalogueBllService,
    private loader: LoaderService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthBllService,
    private homeBll: HomeBllService
  ) {}

  ngOnInit(): void {
    this.loader.show();

     this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    const pavilionDetails =
      this.catalogueBllService.getSelectedPavilionDetails();
    if (pavilionDetails) {
      this.pavilionImgUrl = pavilionDetails.pavilionImgUrl || "";
      this.pavilionName = pavilionDetails.pavilionName || "";
    }

    const categoryDetails =
      this.catalogueBllService.getSelectedCategoryDetails();
    if (categoryDetails) {
      this.categoryName = categoryDetails.categoryName || "";
      this.categoryImgUrl = categoryDetails.categoryImgUrl || "";
    }

    this.route.queryParamMap.subscribe((params) => {
      this.paramsToFetchData.pavilion_code = params.get("pavilion_code") ?? "";
      this.paramsToFetchData.category = params.get("category_id") ?? "";
      this.paramsToFetchData.search = params.get("search") || "";

      this.searchText = this.paramsToFetchData.search || "";

      if (this.paramsToFetchData.pavilion_code) {
        this.getCompaniesList(this.paramsToFetchData);
        this.handlePavilionLoad(this.paramsToFetchData); // Fetch pavilion list on init
      } else if (this.paramsToFetchData.category) {
        this.getCompaniesList(this.paramsToFetchData);
      } else if (this.paramsToFetchData.search) {
        this.getCompaniesList(this.paramsToFetchData);
      } else {
        this.getCompaniesList();
      }

    });
   
    if (this.isAuthenticated) {
      this.fetchUserEventStatus(); 
    }
    this.loadEventDetails();
  }

  /**
   *
   * @returns A promise that resolves when the user event status is fetched and user profile is updated.
   */
  fetchUserEventStatus() {
    return this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
          });
        }
      })
      .catch((err: APIErrorModel) => {
      });
  }

  /**
   * @description Fetches the list of companies based on the provided parameters.
   */
  getCompaniesList(params?: ParamsToGetCompanyList, append: boolean = false): void {

   if (append) {
      this.isLoadingMore = true;  // show spinner for infinite scroll
    } else {
      this.isLoaded = true;      // show global loader for first load
      this.loader.show();
    }

    this.catalogueBllService
      .fetchCompaniesList(params)
      .then((response) => this.handleCompaniesListResponse(response, append))

      .catch((error: APIErrorModel) => {
      })
      .finally(() => {
         // hide loader/spinner depending on call type
        if (append) {
          this.isLoadingMore = false;
        } else {
          this.isLoaded = false;
          this.loader.hide();
        }
      });
  }

  /**
   *
   * @description Handles the response from fetching the companies list.
   */
  handleCompaniesListResponse(response: BaseResponse<CompanyListModel>, append: boolean): void {
    // Handle the response data as needed
    this.paginationParams.pagesCount = response?.data.pagescount;
    this.paginationParams.totalCount = response?.data.totalcount;
    this.paginationParams.startItem = response?.data.startitem;
    this.paginationParams.endItem = response?.data.enditem;
    this.paramsToFetchData.currentpage = response?.data.currentpage;
    this.paramsToFetchData.max_id = response?.data.max_id;
    this.paramsToFetchData.shuffled_index1 = response?.data.shuffled_index1;
    this.paramsToFetchData.shuffled_index2 = response?.data.shuffled_index2;
     const newList = response?.data.list.map(
      (company: listCompanyModel) => {
        return {
          ...company,
          stand_numbers:
            company.stand_numbers?.replace(/,\s*/g, ", ") ||
            company.stand_numbers,
        };
      }
    );

    this.companiesList = {
      ...response.data,
      list: append ? [...(this.companiesList?.list || []), ...newList] : newList
    };

    this.isDataLoaded = true; // set to true after data is loaded
  }

  /**
   * @method trackByIndex
   * @description Tracks items in a list by their index.
   * @param {number} index - The index of the item.
   * @returns {number} The index of the item.
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   *
   * @description Navigates to the company details page with the given company ID.
   */
  goToCompanyDetails(companyId: string, userId: number): void {

    if (!this.isAuthenticated) {
      this.authService.saveStorageValue("state", `/catalogue/company-details?id=${companyId}&userId=${userId}`);
      this.router.navigate(["/auth/init"]);
    } else {
      // Navigate to the company details page using the company ID
      this.router.navigate(["catalogue/company-details"], {
        queryParams: {
          id: companyId,
          userId: userId,
        },
      });
    }
     
  }

  /**
   * @description Navigates to the products list page with the current filters.
   */
  goToProductsList(): void {
    const queryParams: any = {};

    if (this.searchText) {
      queryParams.search = this.searchText;
    }

    if (this.paramsToFetchData.category) {
      queryParams.category_id = this.paramsToFetchData.category;
    }

    if (this.paramsToFetchData.pavilion_code) {
      queryParams.pavilion_code = this.paramsToFetchData.pavilion_code;
    }

    this.router.navigate(["/catalogue/products-list"], { queryParams });
  }

  /**
   *
   * @description Handles page change event for pagination.
   */
  onPageChange(page: number): void {
    this.loader.show();
    const params = {
      ...this.paramsToFetchData,
      currentpage: page,
    };
    this.getCompaniesList(params);
  }

  /**
   *
   * @description Opens the floating search box and focuses the input field.
   */
  openFloatingSearch() {
    this.isSearchOpen = true;

    // Focus textbox after animation
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 300);
  }

  /**
   * @description Closes the floating search box and clears the search text.
   */
  closeFloatingSearch() {
    this.isSearchOpen = false;
    this.searchText = "";
    if (this.paramsToFetchData.hasOwnProperty('search')) {
      delete this.paramsToFetchData.search;
    }

    // Reset parameters and fetch data without search filter
    this.resetParametersToFetchData();

    this.getCompaniesList(this.paramsToFetchData);

    if (
      !this.paramsToFetchData.category &&
      !this.paramsToFetchData.pavilion_code &&
      !this.paramsToFetchData.search
    ) {
      this.router.navigate(["/catalogue/pavilion-list"]);
    }
  }

  /**
   *
   * @description Checks if the company has stand numbers assigned.
   * @param company The company object to check.
   * @returns True if stand numbers exist, false otherwise.
   */
  hasStandNumbers(company: any): boolean {
    return !!company?.stand_numbers; // returns true if not null, undefined, or empty
  }

  openWishlist(): void {
    this.router.navigate(["/catalogue/company-wishlist"]);
  }

  /**
   * @description Handles input event on the search box with debounce.
   */
  onSearchInput() {
    const search = this.searchText.trim();
    
    // Reset parameters for new search
    this.resetParametersToFetchData(search);

    // Clear previous timer
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }

    this.searchDebounce = setTimeout(() => {
      // Prevent calling API with empty search
      this.getCompaniesList(this.paramsToFetchData);
    }, 500);
  }

  /**
   * @description Handles click event on the search icon.
   */
  onSearchIconClick() {
    const search = this.searchText.trim();
    
    // Reset parameters for new search
    this.resetParametersToFetchData(search);

    // Don't trigger API if search is empty
    if (!search) return;
    this.getCompaniesList(this.paramsToFetchData);
    // Cancel the debounce → otherwise double API will fire
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  }

  /**
   * @description Smoothly scrolls the window to the top of the page.
   */
  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  /**
   * clear the debounce timer on component destroy
   */
  ngOnDestroy(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
  }

  onClickBack() {
    if (
      this.paramsToFetchData.category ||
      this.route.snapshot.queryParamMap.get("tab") === "areaofinterest"
    ) {
      this.router.navigate(["catalogue/pavilion-list"], {
        queryParams: { tab: "areaofinterest" },
      });
    } else {
      this.router.navigate(["catalogue/pavilion-list"]);
    }
  }

// Triggered when user scrolls down
  onScrollDown() {
    // Prevent multiple simultaneous calls
    if (this.isLoaded || this.isLoadingMore) return;

    if (!this.paramsToFetchData.currentpage) return;

    // Only call API if there are more pages
    if (this.paramsToFetchData.currentpage < this.paginationParams.pagesCount) {
      const nextParams: ParamsToGetCompanyList = {
        ...this.paramsToFetchData,
        currentpage: this.paramsToFetchData.currentpage + 1,
        shuffled_index1: this.paramsToFetchData.shuffled_index1,
        shuffled_index2: this.paramsToFetchData.shuffled_index2,
      };

      // Use load more spinner for infinite scroll
      this.getCompaniesList(nextParams, true); // append items
    }
  }
  viewLayout(){
    this.catalogueBllService.openLayout();
  }

  /**
   * funtion to handle pavilion load and set pavilion details
   * @param params 
   * @returns 
   */
  handlePavilionLoad(params?: ParamsToGetCompanyList) {
    const stored = this.catalogueBllService.getSelectedPavilionDetails();

    // If stored pavilion matches the code → use it
    if (stored && stored.pavilionCode === params?.pavilion_code) {
      this.pavilionName = stored.pavilionName || "";
      this.pavilionImgUrl = stored.pavilionImgUrl || "";
      return;
    }

    // Otherwise fetch and store
    this.catalogueBllService.fetchPavilionList()
      .then(res => {
        const allPavilions = res.data.pavilion_groups.flatMap(
          group => group.pavilions
        );

        const pavilion = allPavilions.find(
          p => p.code?.toLowerCase() === params?.pavilion_code?.toLowerCase()
        );

        if (!pavilion) return; 

        const data = {
          pavilionCode: params?.pavilion_code,
          pavilionName: pavilion.name || "",
          pavilionImgUrl: pavilion.pavilion_hover_image_url || ""
        };

        // Set in UI
        this.pavilionName = data.pavilionName;
        this.pavilionImgUrl = data.pavilionImgUrl;

      })
      .catch(() => {
      });
  }

  goToTicketPage(){
    this.router.navigate(["reg/select-ticket"]);
  }

  // reset parameters to fetch data
  resetParametersToFetchData(search?: string) {
    this.paramsToFetchData = {
      pavilion_code: this.paramsToFetchData.pavilion_code,
      shuffled_index1: 0,
      shuffled_index2: 0,
      max_id: 0,
      currentpage: 1,
      search: search,
      category: this.paramsToFetchData.category
    }
  }

  loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;      
        
         this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });
        
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }
  
}